import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { FileIcon, FolderIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Skeleton } from "../../ui/skeleton";

import type { FileFolder } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export { FileFolder };

type FileFolderCardProps = FileFolder &
  BaseEntityCardProps & {
    /**
     * When true the card fills its parent container (e.g. React Flow node)
     * and renders as a thumbnail tile instead of a list row. Mirrors the
     * `embedded` prop behaviour of `FileRecordCard`.
     */
    embedded?: boolean;
    /**
     * When false the footer strip (icon + title + time) is hidden in
     * embedded mode. Has no effect in row (non-embedded) mode.
     * Defaults to true.
     */
    showFooter?: boolean;
  };

export function FileFolderCardAvatar({
  icon_link,
  title,
  className,
  style,
}: Partial<FileFolderCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      {icon_link ? (
        <img src={icon_link} alt={title} className="h-5 w-5 object-cover" />
      ) : (
        <FolderIcon className="h-5 w-5 text-muted-foreground" />
      )}
    </BaseEntityCardIcon>
  );
}

/**
 * Rewrites a Google thumbnail URL's trailing size token (`=sNNN`) to a
 * larger value so canvas tiles look crisp without a backend round-trip.
 * Returns the URL unchanged when the pattern is not found.
 */
function upscaleThumbnail(url: string, size = 640): string {
  return url.replace(/=s\d+(-[^?]*)?(\?.*)?$/, (_, flags, qs) => {
    const suffix = flags ? `=s${size}${flags}` : `=s${size}`;
    return qs ? `${suffix}${qs}` : suffix;
  });
}

/**
 * Drive's `icon_link` points at a 16px asset
 * (`drive-thirdparty.googleusercontent.com/16/type/...`). Google serves the
 * identical icons at 32/64/128px — swap the size path segment so placeholder
 * icons render crisp instead of a stretched 16px bitmap.
 */
function upscaleIcon(url: string, size = 64): string {
  return url.replace(
    /(drive-thirdparty\.googleusercontent\.com)\/\d+\//,
    `$1/${size}/`,
  );
}

/**
 * Candidate thumbnail sources for an embedded tile, best first.
 *
 * The stored `thumbnail_link` is a short-lived signed URL (expires within
 * hours of the entity being fetched), so nodes placed on a canvas days ago
 * 403 on it forever. Drive's live thumbnail endpoint
 * (`drive.google.com/thumbnail?id=…`) never expires — it authenticates via
 * the browser's Google session (the same dependency our preview iframes
 * already have) and redirects to a freshly signed lh3 URL. We try it first
 * and keep the stored link as a cookie-less fallback for recently fetched
 * entities.
 */
function buildThumbnailCandidates({
  platform,
  id,
  thumbnail_link,
}: {
  platform?: string;
  id?: string;
  thumbnail_link?: string;
}): string[] {
  const candidates: string[] = [];
  if (platform === "google-drive" && id) {
    candidates.push(
      `https://drive.google.com/thumbnail?id=${encodeURIComponent(id)}&sz=w640`,
    );
  }
  if (thumbnail_link) {
    candidates.push(upscaleThumbnail(thumbnail_link));
  }
  return candidates;
}

export function FileFolderCard({
  platform,
  id,
  title,
  icon_link,
  thumbnail_link,
  updated_at,
  web_link,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  embedded = false,
  showFooter = true,
  className,
  style,
}: FileFolderCardProps) {
  const thumbCandidates = useMemo(
    () => buildThumbnailCandidates({ platform, id, thumbnail_link }),
    [platform, id, thumbnail_link],
  );
  // Index into thumbCandidates; advances on load error until exhausted.
  const [thumbIndex, setThumbIndex] = useState(0);
  const [thumbLoaded, setThumbLoaded] = useState(false);

  // Restart the fallback chain when the entity payload refreshes (e.g. a
  // re-fetch lands a fresh signed thumbnail_link).
  useEffect(() => {
    setThumbIndex(0);
    setThumbLoaded(false);
  }, [thumbCandidates]);

  // ── Embedded (canvas tile) mode ──────────────────────────────────────────
  if (embedded) {
    const thumbSrc =
      thumbIndex < thumbCandidates.length
        ? thumbCandidates[thumbIndex]
        : undefined;
    const crispIcon = icon_link ? upscaleIcon(icon_link) : undefined;

    // Placeholder stays mounted underneath the thumbnail and the thumbnail
    // fades in over it on load — no blank frame, no layout jump.
    const placeholder = (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-muted/40 to-muted/70">
        <div
          className="flex items-center justify-center rounded-2xl bg-background shadow-sm ring-1 ring-border/60"
          style={{
            width: "clamp(48px, 30cqw, 80px)",
            height: "clamp(48px, 30cqw, 80px)",
          }}
        >
          {crispIcon ? (
            <img
              src={crispIcon}
              alt={title ?? ""}
              style={{
                width: "clamp(24px, 16cqw, 44px)",
                height: "clamp(24px, 16cqw, 44px)",
              }}
              className="object-contain"
            />
          ) : (
            <FileIcon
              className="text-muted-foreground"
              style={{
                width: "clamp(20px, 14cqw, 36px)",
                height: "clamp(20px, 14cqw, 36px)",
                strokeWidth: 1.5,
              }}
            />
          )}
        </div>
      </div>
    );

    const thumbWell = (
      <div className="relative flex-1 min-h-0 w-full overflow-hidden bg-card">
        {!(thumbSrc && thumbLoaded) && placeholder}
        {thumbSrc && (
          <img
            src={thumbSrc}
            alt={title ?? ""}
            loading="eager"
            decoding="async"
            referrerPolicy="no-referrer"
            className={cn(
              "absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-200",
              thumbLoaded ? "opacity-100" : "opacity-0",
            )}
            onLoad={() => setThumbLoaded(true)}
            onError={() => {
              setThumbLoaded(false);
              setThumbIndex((i) => i + 1);
            }}
          />
        )}
      </div>
    );

    const footerEl = showFooter && (
      <div className="flex items-center gap-2 shrink-0 px-2.5 py-2 border-t bg-card/80">
        {icon_link && (
          <img
            src={icon_link}
            alt=""
            className="h-3.5 w-3.5 shrink-0 object-contain"
          />
        )}
        <p className="flex-1 min-w-0 text-[12px] font-semibold text-foreground leading-tight truncate">
          {title}
        </p>
        {updated_at && (
          <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
            {formatDistanceToNow(new Date(updated_at), { addSuffix: true })}
          </span>
        )}
      </div>
    );

    return (
      <BaseEntityCard
        layout="stack"
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        processes={processes}
        className={cn(
          "group relative overflow-hidden transition-all duration-200",
          "w-full h-full min-h-0 rounded-xl border-0 shadow-none hover:shadow-2xl flex flex-col bg-card [&>div]:flex [&>div]:flex-1 [&>div]:min-h-0 [&>div]:flex-col",
          className,
        )}
        style={{ containerType: "inline-size", ...style }}
      >
        {thumbWell}
        {footerEl}
      </BaseEntityCard>
    );
  }

  // ── Row (list) mode — unchanged ──────────────────────────────────────────
  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <FileFolderCardAvatar icon_link={icon_link} title={title} />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>{title}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {updated_at &&
                formatDistanceToNow(new Date(updated_at), {
                  addSuffix: true,
                })}
            </span>
            {web_link && (
              <BaseEntityCardExternalLinkAction
                href={web_link}
                tooltip="Open document"
              />
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

export function FileFolderCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCard className={cn("h-[90px]", className)} style={style}>
      <Skeleton className="h-10 w-10 rounded-lg" />
      <BaseEntityCardContent>
        <Skeleton className="h-4 w-1/3 mt-1.5 mb-1.5" />
        <Skeleton className="h-4 w-3/4 mb-1.5" />
        <Skeleton className="h-3 w-1/2" />
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
